import {
  DATES_OPTION_FILTER,
  DateSelect,
  FILTER_ALL_VALUE,
  TRANSACTIONS_STATUS_FILTER
} from '@namespace/common';
import { DateColumn } from '../../common/TransactionsHistory/DateColumn';
import { ReceiptColumn } from '../../common/TransactionsHistory/ReceiptColumn';
import { StatusColumn } from '../../common/TransactionsHistory/StatusColumn';
import { WalletColumn } from '../../common/TransactionsHistory/WalletColumn';
import { PAYMENT_TYPES } from '../../constants';
import {
  AMOUNT_FILTER,
  DEPOSIT_AMOUNT_FILTER,
  FILTER_FIELDS,
  WITHDRAW_AMOUNT_FILTER
} from '../../constants/filter';
import { Option, PaymentType } from '../../types/common';
import { FilterField, SourceItem } from '../../types/history';

interface GetFilterFieldsConfigParams {
  t: (key: string) => string;
  usedSidList: Option[];
  type: 'deposit' | 'withdraw';
  fields?: readonly FilterField[];
  amountFilterOptions: Option[];
  maxMinDate?: unknown;
}

interface FieldComponentProps {
  defaultValue?: string | number;
  [key: string]: unknown;
}

interface FieldConfig {
  key: string;
  dataRole: string;
  label: string;
  options: Option[];
  Component?: React.FC<FieldComponentProps>;
}

export const getFilterFieldsConfig = ({
  t,
  usedSidList,
  type,
  fields = FILTER_FIELDS,
  amountFilterOptions,
  maxMinDate
}: GetFilterFieldsConfigParams): FieldConfig[] => {
  const fieldsConfig: Record<FilterField, FieldConfig> = {
    status: {
      key: 'status',
      dataRole: 'payments-state-filter',
      label: 'web.balance.deposithistory.filter.status',
      options: TRANSACTIONS_STATUS_FILTER
    },
    dateOptionId: {
      key: 'dateOptionId',
      dataRole: 'payments-dateOptionId-filter',
      label: 'web.balance.deposithistory.filter.daterange',
      options: DATES_OPTION_FILTER,
      Component: ({
        defaultValue,
        ...rest
      }: FieldComponentProps): React.ReactElement => (
        <DateSelect
          placeholders={{
            from: t('web.balance.deposithistory.filter.daterange.start'),
            to: t('web.balance.deposithistory.filter.daterange.end')
          }}
          value={defaultValue}
          {...rest}
          isWithDateRange={true}
          maxMinDate={maxMinDate}
        />
      )
    },
    wallet: {
      key: 'wallet',
      dataRole: 'payments-wallet-filter',
      label: 'web.balance.deposithistory.filter.wallet',
      options: [
        {
          value: FILTER_ALL_VALUE,
          label: 'web.balance.deposithistory.filter_all'
        },
        ...usedSidList
      ]
    },
    amount: {
      key: 'amount',
      dataRole: 'payments-amount-filter',
      label: 'balance.depositHistory.filter.amount',
      options: amountFilterOptions || getAmountFilter(type)
    }
  };

  return fields.map((fieldKey) => fieldsConfig[fieldKey]);
};

export interface FormattedItem {
  id: string;
  objectId: string;
  walletId: string;
  cardMask: string;
  status: React.ReactElement;
  date: React.ReactElement;
  wallet: React.ReactElement;
  details: string;
  amount: string;
  isChild?: boolean;
  receipt: React.ReactElement;
}

export const formatData = (
  source: SourceItem[],
  type: string
): FormattedItem[] =>
  source.map(
    ({
      amount,
      details,
      walletId,
      serviceId,
      currency,
      cardMask,
      objectId,
      id,
      isSplittedChild,
      checkLink,
      refundCheckLink,
      ...rest
    }) => ({
      id,
      objectId,
      walletId,
      cardMask,
      status: <StatusColumn type={type} {...rest} />,
      date: <DateColumn {...rest} />,
      wallet: <WalletColumn serviceId={serviceId} />,
      details,
      amount: `${amount} ${currency}`,
      isChild: isSplittedChild,
      receipt: (
        <ReceiptColumn
          receiptUrl={checkLink}
          receiptReturnUrl={refundCheckLink}
        />
      )
    })
  );

export const getAmountFilter = (type: PaymentType): Option[] => {
  const isDeposit = type === PAYMENT_TYPES.DEPOSIT;
  const filterOptionsByType = isDeposit
    ? DEPOSIT_AMOUNT_FILTER
    : WITHDRAW_AMOUNT_FILTER;
  return [...AMOUNT_FILTER, ...filterOptionsByType];
};
